/**
 * @module CanvasReasoningPanelState
 * @purpose Pure body-state, dialog-variant and launch-lock derivations for
 *   CanvasReasoningPanel -- split out to keep that file under the 500-line budget.
 * @depends types/reasoning, ForceRecomputeDialog (variant type only)
 * @dependedOnBy CanvasReasoningPanel
 *
 * @rules
 * - Performance: pure functions -- no data fetching, no React
 * - State: n/a
 * - Errors: n/a
 */

import type { ForceRecomputeDialogVariant } from './ForceRecomputeDialog'
import type { ReasoningRun, ReasoningRunStatus } from '../../../../types/reasoning'

/**
 * Human-readable form for run timestamps (defect 3, Duncan 2026-09-05: raw
 * ISO strings with microseconds reached the panel). Matches the house
 * en-GB shape used by the receipt components. Falls back to the raw string
 * for anything Date cannot parse -- never hides the value.
 */
export function formatRunTimestamp(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export type BodyState =
  | 'loading'
  | 'error'
  | 'running'
  | 'failed'
  | 'notStarted'
  | 'empty'
  | 'list'
  | 'emptyWithBanner'
  | 'listWithBanner'

/** Run statuses that mean the engine is still working. */
const ACTIVE_RUN_STATUSES = new Set(['pending', 'running'])

/**
 * Body states whose BODY renders a run OTHER than the latest one -- these
 * read header data and cluster ids from `completedRun`, not `run`. Plain
 * 'list' is deliberately absent: it is only reached when `run.status ===
 * 'completed'`, and `deriveCompletedRun` (use-reasoning.ts) then returns
 * `run` itself as `completedRun` -- the two are the same object, so reading
 * `run` there is both correct and the smaller diff against every existing
 * mocked test that only sets `run`.
 */
export const COMPLETED_OUTCOME_BODY_STATES: ReadonlySet<BodyState> = new Set([
  'listWithBanner',
  'emptyWithBanner',
])

export interface BodyStateInput {
  /** True the instant a launch POST is in flight — takes priority over every
   *  other input so the body swap itself is the launch's busy feedback. */
  isComputing: boolean
  loading: boolean
  error: string | null
  clusterCount: number | null | undefined
  runStatus: string | undefined
  /** False when this case has never had a reasoning run created — distinct
   *  from a completed run that surfaced zero clusters. */
  runExists: boolean
  /** The most recently completed run for this case, independent of whether
   *  `run` (the latest one) is itself completed. See use-reasoning.ts's
   *  run-vs-completedRun contract. */
  completedRun: ReasoningRun | null
}

export function isActiveRunStatus(runStatus: string | undefined): boolean {
  return runStatus !== undefined && ACTIVE_RUN_STATUSES.has(runStatus)
}

export function isFailedOrSupersededStatus(runStatus: string | undefined): boolean {
  return runStatus === 'failed' || runStatus === 'superseded'
}

export function isEmptyClusterCount(clusterCount: number | null | undefined): boolean {
  return clusterCount === null || clusterCount === undefined || clusterCount === 0
}

export function resolveBodyState({
  isComputing,
  loading,
  error,
  clusterCount,
  runStatus,
  runExists,
  completedRun,
}: BodyStateInput): BodyState {
  if (isComputing) return 'running'
  if (loading) return 'loading'
  if (error !== null) return 'error'
  // Must precede the empty check: an in-flight run has a null clusterCount, and
  // reporting that as "no clusters surfaced" told analysts the case was empty
  // for the several minutes the engine was actually running.
  if (isActiveRunStatus(runStatus)) return 'running'
  // Must also precede the empty check, for the same reason: a failed/superseded
  // run has a null clusterCount, and "no clusters surfaced" asserts the engine
  // ran and found nothing. It did not run. An interrupted run reported as an
  // empty case is worse than an error — the analyst stops looking. When a
  // completed run still exists underneath it, its results surface WITH the
  // failed banner rather than being hidden behind the plain failed state.
  if (isFailedOrSupersededStatus(runStatus)) {
    if (completedRun === null) return 'failed'
    return isEmptyClusterCount(completedRun.clusterCount) ? 'emptyWithBanner' : 'listWithBanner'
  }
  // A case that has never been reasoned over is not "empty" -- it is
  // unstarted. loading is false and error is null here (both branches above
  // already returned), so this reads only the run's existence.
  if (!runExists) return 'notStarted'
  if (isEmptyClusterCount(clusterCount)) return 'empty'
  return 'list'
}

/**
 * Which confirmation copy is honest for the current latest run (FR5).
 *
 * Only reached when the trigger is unlocked, so `runUnknown` and the initial
 * loading window never produce a variant here -- the lock disables the
 * trigger in both.
 */
export function resolveDialogVariant(runStatus: ReasoningRunStatus | undefined): ForceRecomputeDialogVariant {
  if (runStatus === 'completed') return 'force'
  if (runStatus === 'failed' || runStatus === 'superseded') return 'retry'
  return 'firstRun'
}

export interface LaunchLockInput {
  isComputing: boolean
  loading: boolean
  error: string | null
  runStatus: ReasoningRunStatus | undefined
  runExists: boolean
}

/**
 * FR2: the four terms that hold the launch trigger locked until a logical
 * end -- a launch in flight, an active run (covers remount), the latest run
 * being unknown (a non-404 GET failure), or the initial fetch still loading.
 */
export function computeLaunchLocked({
  isComputing,
  loading,
  error,
  runStatus,
  runExists,
}: LaunchLockInput): boolean {
  const runActive = isActiveRunStatus(runStatus)
  const runUnknown = !runExists && error !== null
  return isComputing || runActive || runUnknown || loading
}
